import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailableQuotaTab = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedFactory, setSelectedFactory] = useState('all');

  // Mock data for available quota
  const quotaData = [
    {
      id: 1,
      factory: 'Shree Ganesh Sugar Mills',
      location: 'Kolhapur, Maharashtra',
      grade: 'S-30',
      availableQuantity: 5000,
      pricePerTon: 38500,
      qualitySpec: 'ICUMSA < 45, Moisture < 0.04%',
      minimumOrder: 100,
      deliveryTerms: 'Ex-Mill',
      validTill: '2025-12-15',
      status: 'available'
    },
    {
      id: 2,
      factory: 'Maharashtra Rajya Sahakari Sugar Factory',
      location: 'Sangli, Maharashtra',
      grade: 'M-30',
      availableQuantity: 3500,
      pricePerTon: 37800,
      qualitySpec: 'ICUMSA 45-60, Moisture < 0.05%',
      minimumOrder: 50,
      deliveryTerms: 'Ex-Mill',
      validTill: '2025-12-20',
      status: 'available'
    },
    {
      id: 3,
      factory: 'Vasantdada Sugar Institute',
      location: 'Pune, Maharashtra',
      grade: 'L-30',
      availableQuantity: 1200,
      pricePerTon: 36500,
      qualitySpec: 'ICUMSA 60-100, Moisture < 0.06%',
      minimumOrder: 25,
      deliveryTerms: 'Ex-Mill',
      validTill: '2025-12-10',
      status: 'limited'
    },
    {
      id: 4,
      factory: 'Dalmia Sugar Mills',
      location: 'Nigoha, Uttar Pradesh',
      grade: 'S-31',
      availableQuantity: 7500,
      pricePerTon: 39200,
      qualitySpec: 'ICUMSA < 40, Moisture < 0.03%',
      minimumOrder: 200,
      deliveryTerms: 'Ex-Mill',
      validTill: '2025-12-25',
      status: 'available'
    },
    {
      id: 5,
      factory: 'Bannari Amman Sugars Ltd',
      location: 'Erode, Tamil Nadu',
      grade: 'M-30',
      availableQuantity: 4200,
      pricePerTon: 38000,
      qualitySpec: 'ICUMSA 45-60, Moisture < 0.05%',
      minimumOrder: 75,
      deliveryTerms: 'Ex-Mill',
      validTill: '2025-12-18',
      status: 'available'
    }
  ];

  const grades = ['all', 'S-30', 'S-31', 'M-30', 'L-30'];
  const factories = ['all', ...new Set(quotaData.map(q => q.factory))];

  const filteredQuota = quotaData?.filter(quota => {
    const matchesSearch = quota?.factory?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         quota?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesGrade = selectedGrade === 'all' || quota?.grade === selectedGrade;
    const matchesFactory = selectedFactory === 'all' || quota?.factory === selectedFactory;
    return matchesSearch && matchesGrade && matchesFactory;
  });

  const totalAvailableQuantity = filteredQuota?.reduce((sum, q) => sum + q?.availableQuantity, 0);
  const averagePrice = filteredQuota?.length > 0 
    ? (filteredQuota?.reduce((sum, q) => sum + q?.pricePerTon, 0) / filteredQuota?.length)?.toFixed(0)
    : 0;

  const getStatusColor = (status) => {
    switch(status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'limited': return 'text-yellow-600 bg-yellow-100';
      case 'sold-out': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'available': return 'CheckCircle2';
      case 'limited': return 'AlertTriangle';
      case 'sold-out': return 'XCircle';
      default: return 'Circle';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Package" size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Available</p>
              <p className="text-2xl font-bold text-foreground">{totalAvailableQuantity?.toLocaleString()} MT</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="Factory" size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Factories</p>
              <p className="text-2xl font-bold text-foreground">{filteredQuota?.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Icon name="IndianRupee" size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Price/MT</p>
              <p className="text-2xl font-bold text-foreground">₹{averagePrice}</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Grades Available</p>
              <p className="text-2xl font-bold text-foreground">{grades?.length - 1}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Search</label>
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search factory or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Grade</label>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
            >
              {grades?.map(grade => (
                <option key={grade} value={grade}>
                  {grade === 'all' ? 'All Grades' : grade}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Factory</label>
            <select
              value={selectedFactory}
              onChange={(e) => setSelectedFactory(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-[#ff8c00] text-foreground"
            >
              {factories?.map(factory => (
                <option key={factory} value={factory}>
                  {factory === 'all' ? 'All Factories' : factory}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Quota Listings */}
      <div className="space-y-4">
        {filteredQuota?.map((quota) => (
          <div key={quota?.id} className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{quota?.factory}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(quota?.status)} flex items-center gap-1`}>
                        <Icon name={getStatusIcon(quota?.status)} size={14} />
                        {quota?.status?.charAt(0)?.toUpperCase() + quota?.status?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="MapPin" size={16} />
                      <span>{quota?.location}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Grade</p>
                    <p className="font-semibold text-foreground">{quota?.grade}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Available Quantity</p>
                    <p className="font-semibold text-foreground">{quota?.availableQuantity?.toLocaleString()} MT</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Price per MT</p>
                    <p className="font-semibold text-green-600">₹{quota?.pricePerTon?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Min. Order</p>
                    <p className="font-semibold text-foreground">{quota?.minimumOrder} MT</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Icon name="Beaker" size={16} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">Quality Spec</p>
                      <p className="text-foreground">{quota?.qualitySpec}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon name="Truck" size={16} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">Delivery Terms</p>
                      <p className="text-foreground">{quota?.deliveryTerms}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon name="Calendar" size={16} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">Valid Till</p>
                      <p className="text-foreground">{new Date(quota.validTill)?.toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex lg:flex-col gap-2 lg:w-48">
                <Button
                  className="flex-1 bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] text-white hover:shadow-lg"
                  onClick={() => {}}
                >
                  <Icon name="ShoppingCart" size={18} />
                  <span>Purchase</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {}}
                >
                  <Icon name="Info" size={18} />
                  <span>Details</span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredQuota?.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Icon name="Search" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Quota Found</h3>
          <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
        </div>
      )}
    </div>
  );
};

export default AvailableQuotaTab;